/**
 * Funnel tracking — fire-and-forget, never throws
 * Client-side uses sendBeacon (survives navigation), server-side uses fetch.
 */

import { abHashVariant } from "./abtest";
import { AB_ACTIVE_TESTS } from "./abActiveTests";

const FUNNEL_API = "https://vexub.fr/api/v3/funnel/track";

export type FunnelEvent =
  | "signup_start" | "signup_complete" | "first_login"
  | "dashboard_view"
  | "create_view" | "create_mode_select" | "create_step_format" | "create_step_narrator"
  | "create_step_script" | "create_step_script_ai" | "create_step_style"
  | "create_step_import_mp3" | "create_step_import_mp4"
  | "create_step_clip_source" | "create_step_clip_youtube_url" | "create_step_clip_duration"
  | "create_upload_start" | "create_upload_complete"
  | "create_submit" | "create_success" | "create_error"
  | "project_view" | "project_poll_status" | "project_generation_complete"
  | "project_upgrade_prompt" | "project_share_social" | "project_delete" | "project_click_edit" | "project_click_reedit"
  | "nav_click"
  | "profil_view" | "profil_tab_switch" | "profil_feedback_send"
  | "profil_click_discord" | "profil_click_intercom" | "profil_click_stripe_portal"
  | "profil_click_logout" | "profil_click_cancel_sub"
  | "cancel_flow_step" | "cancel_flow_keep" | "cancel_flow_offer_accept" | "cancel_flow_confirm_cancel"
  | "affiliate_view" | "affiliate_become" | "affiliate_create_link"
  | "affiliate_set_paypal" | "affiliate_open_dashboard" | "affiliate_share_click"
  | "pricing_view" | "pricing_engagement" | "plan_click" | "checkout_start" | "purchase_complete"
  | "view_estimate_shown" | "view_estimate_cta_click";

interface TrackOptions {
  properties?: Record<string, unknown>;
  email?: string;
  anonymous_id?: string;
  abonnement?: number;
  source?: string;
}

function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : undefined;
}

function getEmailFromJWT(): string | undefined {
  const token = getCookie("token");
  if (!token) return undefined;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.email || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Auto-collected context for every event — enables correlation analysis
 */
function getContext(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  try {
    const ua = navigator.userAgent;
    const ctx: Record<string, unknown> = {};

    // Device & screen
    ctx.device = /Mobi|Android/i.test(ua) ? "mobile" : /Tablet|iPad/i.test(ua) ? "tablet" : "desktop";
    ctx.browser = /Firefox/i.test(ua) ? "Firefox" : /Edg/i.test(ua) ? "Edge" : /Chrome/i.test(ua) ? "Chrome" : /Safari/i.test(ua) ? "Safari" : "other";
    ctx.os = /iPhone|iPad|iPod/i.test(ua) ? "iOS" : /Android/i.test(ua) ? "Android" : /Mac/i.test(ua) ? "Mac" : /Win/i.test(ua) ? "Windows" : /Linux/i.test(ua) ? "Linux" : "other";
    ctx.screen_w = window.innerWidth;
    ctx.screen_h = window.innerHeight;

    // Time
    ctx.hour = new Date().getHours();
    ctx.weekday = new Date().getDay(); // 0=Sun
    try { ctx.tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch {}

    // Session (persists across pages within one browser session)
    let sid = sessionStorage.getItem("f_sid");
    if (!sid) {
      sid = Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem("f_sid", sid);
      sessionStorage.setItem("f_t0", Date.now().toString());
    }
    ctx.session_id = sid;
    ctx.session_sec = Math.round((Date.now() - Number(sessionStorage.getItem("f_t0"))) / 1000);

    // Locale from URL
    const lm = window.location.pathname.match(/^\/(en|fr)\//);
    ctx.locale = lm ? lm[1] : "en";

    // UTM params (capture first-touch, persist in session)
    const sp = new URLSearchParams(window.location.search);
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
      const v = sp.get(k);
      if (v) sessionStorage.setItem(k, v);
      const stored = sessionStorage.getItem(k);
      if (stored) ctx[k] = stored;
    }

    // First-touch referrer (external only)
    if (!sessionStorage.getItem("f_ref") && document.referrer) {
      try {
        const ref = new URL(document.referrer);
        if (ref.hostname !== window.location.hostname) {
          sessionStorage.setItem("f_ref", document.referrer);
        }
      } catch {}
    }
    const firstRef = sessionStorage.getItem("f_ref");
    if (firstRef) ctx.first_referrer = firstRef;

    // Time on current page (seconds since page load)
    if (typeof performance !== "undefined") {
      ctx.page_sec = Math.round(performance.now() / 1000);
    }

    // AB test variants — deterministic, no API call
    const abUserId = getCookie("ab_user_id") || getCookie("user_id_com");
    if (abUserId && Object.keys(AB_ACTIVE_TESTS).length > 0) {
      const abVariants: Record<string, string> = {};
      for (const [testName, weights] of Object.entries(AB_ACTIVE_TESTS)) {
        abVariants[testName] = abHashVariant(abUserId, testName, weights);
      }
      ctx.ab_variants = abVariants;
    }

    return ctx;
  } catch {
    return {};
  }
}

/**
 * Client-side tracking — uses sendBeacon for reliability
 * Auto-enriches every event with device, session, UTM, screen, time context
 */
export function trackFunnel(event: FunnelEvent, options?: TrackOptions): void {
  try {
    const email = options?.email || getEmailFromJWT();
    const anonymousId = options?.anonymous_id || getCookie("ab_user_id") || getCookie("user_id_com");
    const autoCtx = getContext();
    const mergedProps = { ...autoCtx, ...(options?.properties || {}) };

    const payload = JSON.stringify({
      event,
      email: email || null,
      anonymous_id: anonymousId || null,
      properties: mergedProps,
      page_path: typeof window !== "undefined" ? window.location.pathname : null,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      country: getCookie("country") || null,
      abonnement: options?.abonnement ?? 0,
    });

    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(FUNNEL_API, new Blob([payload], { type: "application/json" }));
    } else {
      fetch(FUNNEL_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Never throw
  }
}

/**
 * Server-side tracking — for RSC / API routes
 */
export async function trackFunnelServer(
  event: FunnelEvent,
  email: string,
  extra?: { properties?: Record<string, unknown>; abonnement?: number; country?: string }
): Promise<void> {
  try {
    await fetch(FUNNEL_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event,
        email,
        anonymous_id: null,
        properties: extra?.properties || null,
        page_path: null,
        referrer: null,
        country: extra?.country || null,
        abonnement: extra?.abonnement ?? 0,
      }),
    });
  } catch {
    // Never throw
  }
}
